// Unlockable cosmetics catalog + helpers.
//
// Items unlock based on `lifetimeRuns` — the total number of runs the
// player has completed (any outcome). Thresholds are tuned so kids see a
// steady drip of new items: a basic starter set (no-hat + solid + pink
// shirt) is unlocked at 0, then a fresh cosmetic appears roughly every
// 1–3 runs early on, slowing down as the catalog widens.

import type { HatId, PatternId, OutfitId, FaceId } from "@shared/types";

export type UnlockKind = "hat" | "pattern" | "shirt-color" | "outfit" | "face";

export interface UnlockItem {
  /** Stable id, also used as the key in profile.unlocked[]. */
  id: string;
  kind: UnlockKind;
  name: string;
  /** Lifetime completed runs required to unlock. 0 = starts unlocked. */
  unlockAt: number;
  /** Applied to AvatarConfig when equipped. */
  apply: {
    hatId?: HatId;
    patternId?: PatternId;
    shirtColor?: string;
    outfitId?: OutfitId;
    faceId?: FaceId;
  };
}

export const UNLOCKS: UnlockItem[] = [
  // ── Hats (14 + base) — only "No Hat" starts unlocked. ───────────────
  { id: "hat-none",        kind: "hat", name: "No Hat",       unlockAt: 0,  apply: { hatId: "none" } },
  { id: "hat-cap-blue",    kind: "hat", name: "Blue Cap",     unlockAt: 1,  apply: { hatId: "cap-blue" } },
  { id: "hat-cap-red",     kind: "hat", name: "Red Cap",      unlockAt: 2,  apply: { hatId: "cap-red" } },
  { id: "hat-cap-green",   kind: "hat", name: "Green Cap",    unlockAt: 4,  apply: { hatId: "cap-green" } },
  { id: "hat-cap-purple",  kind: "hat", name: "Purple Cap",   unlockAt: 6,  apply: { hatId: "cap-purple" } },
  { id: "hat-beanie",      kind: "hat", name: "Pink Beanie",  unlockAt: 8,  apply: { hatId: "beanie" } },
  { id: "hat-beanie-blue", kind: "hat", name: "Blue Beanie",  unlockAt: 11, apply: { hatId: "beanie-blue" } },
  { id: "hat-headband",    kind: "hat", name: "Headband",     unlockAt: 14, apply: { hatId: "headband" } },
  { id: "hat-party",       kind: "hat", name: "Party Hat",    unlockAt: 18, apply: { hatId: "party-hat" } },
  { id: "hat-bow",         kind: "hat", name: "Pink Bow",     unlockAt: 22, apply: { hatId: "bow" } },
  { id: "hat-top-hat",     kind: "hat", name: "Top Hat",      unlockAt: 27, apply: { hatId: "top-hat" } },
  { id: "hat-horns",       kind: "hat", name: "Devil Horns",  unlockAt: 32, apply: { hatId: "horns" } },
  { id: "hat-halo",        kind: "hat", name: "Halo",         unlockAt: 38, apply: { hatId: "halo" } },
  { id: "hat-wizard",      kind: "hat", name: "Wizard Hat",   unlockAt: 45, apply: { hatId: "wizard" } },
  { id: "hat-crown",       kind: "hat", name: "Golden Crown", unlockAt: 55, apply: { hatId: "crown" } },
  // v2 hats — fun-themed.
  { id: "hat-chef",        kind: "hat", name: "Chef Hat",     unlockAt: 3,  apply: { hatId: "chef" } },
  { id: "hat-cowboy",      kind: "hat", name: "Cowboy Hat",   unlockAt: 5,  apply: { hatId: "cowboy" } },
  { id: "hat-propeller",   kind: "hat", name: "Propeller",    unlockAt: 9,  apply: { hatId: "propeller" } },
  { id: "hat-bunny-ears",  kind: "hat", name: "Bunny Ears",   unlockAt: 13, apply: { hatId: "bunny-ears" } },
  { id: "hat-fox-ears",    kind: "hat", name: "Fox Ears",     unlockAt: 17, apply: { hatId: "fox-ears" } },
  { id: "hat-pirate",      kind: "hat", name: "Pirate Hat",   unlockAt: 21, apply: { hatId: "pirate" } },
  { id: "hat-viking",      kind: "hat", name: "Viking Helm",  unlockAt: 28, apply: { hatId: "viking" } },
  { id: "hat-sun-hat",     kind: "hat", name: "Sun Hat",      unlockAt: 35, apply: { hatId: "sun-hat" } },

  // ── Patterns (8) — only "Solid" starts unlocked. ───────────────────
  { id: "pattern-solid",     kind: "pattern", name: "Solid",      unlockAt: 0,  apply: { patternId: "solid" } },
  { id: "pattern-stripes",   kind: "pattern", name: "Stripes",    unlockAt: 3,  apply: { patternId: "stripes" } },
  { id: "pattern-dots",      kind: "pattern", name: "Polka Dots", unlockAt: 7,  apply: { patternId: "dots" } },
  { id: "pattern-zigzag",    kind: "pattern", name: "Zigzag",     unlockAt: 12, apply: { patternId: "zigzag" } },
  { id: "pattern-checker",   kind: "pattern", name: "Checkered",  unlockAt: 17, apply: { patternId: "checker" } },
  { id: "pattern-hearts",    kind: "pattern", name: "Hearts",     unlockAt: 24, apply: { patternId: "hearts" } },
  { id: "pattern-star",      kind: "pattern", name: "Star Power", unlockAt: 32, apply: { patternId: "star" } },
  { id: "pattern-lightning", kind: "pattern", name: "Lightning",  unlockAt: 42, apply: { patternId: "lightning" } },
  // v2 patterns.
  { id: "pattern-camo",      kind: "pattern", name: "Camo",       unlockAt: 6,  apply: { patternId: "camo" } },
  { id: "pattern-rainbow",   kind: "pattern", name: "Rainbow",    unlockAt: 10, apply: { patternId: "rainbow" } },
  { id: "pattern-flame",     kind: "pattern", name: "Flame",      unlockAt: 16, apply: { patternId: "flame" } },
  { id: "pattern-plaid",     kind: "pattern", name: "Plaid",      unlockAt: 20, apply: { patternId: "plaid" } },
  { id: "pattern-galaxy",    kind: "pattern", name: "Galaxy",     unlockAt: 30, apply: { patternId: "galaxy" } },

  // ── Outfits (overlays on the shirt) — 8 total. ─────────────────────
  { id: "outfit-none",   kind: "outfit", name: "No Outfit", unlockAt: 0,  apply: { outfitId: "none" } },
  { id: "outfit-vest",   kind: "outfit", name: "Vest",     unlockAt: 4,  apply: { outfitId: "vest" } },
  { id: "outfit-hoodie", kind: "outfit", name: "Hoodie",   unlockAt: 8,  apply: { outfitId: "hoodie" } },
  { id: "outfit-apron",  kind: "outfit", name: "Apron",    unlockAt: 12, apply: { outfitId: "apron" } },
  { id: "outfit-scarf",  kind: "outfit", name: "Scarf",    unlockAt: 16, apply: { outfitId: "scarf" } },
  { id: "outfit-jacket", kind: "outfit", name: "Jacket",   unlockAt: 20, apply: { outfitId: "jacket" } },
  { id: "outfit-cape",   kind: "outfit", name: "Cape",     unlockAt: 26, apply: { outfitId: "cape" } },
  { id: "outfit-robe",   kind: "outfit", name: "Robe",     unlockAt: 34, apply: { outfitId: "robe" } },

  // ── Face accessories — 7 total. ────────────────────────────────────
  { id: "face-none",        kind: "face", name: "No Glasses",  unlockAt: 0,  apply: { faceId: "none" } },
  { id: "face-glasses",     kind: "face", name: "Glasses",     unlockAt: 6,  apply: { faceId: "glasses" } },
  { id: "face-sunglasses",  kind: "face", name: "Sunglasses",  unlockAt: 10, apply: { faceId: "sunglasses" } },
  { id: "face-star-shades", kind: "face", name: "Star Shades", unlockAt: 15, apply: { faceId: "star-shades" } },
  { id: "face-monocle",     kind: "face", name: "Monocle",     unlockAt: 22, apply: { faceId: "monocle" } },
  { id: "face-mustache",    kind: "face", name: "Mustache",    unlockAt: 30, apply: { faceId: "mustache" } },
  { id: "face-eyepatch",    kind: "face", name: "Eyepatch",    unlockAt: 38, apply: { faceId: "eyepatch" } },

  // ── Shirt colors (16) — only "Hot Pink" starts unlocked. ───────────
  { id: "color-pink",       kind: "shirt-color", name: "Hot Pink",     unlockAt: 0,  apply: { shirtColor: "#ff2ca4" } },
  { id: "color-sky",        kind: "shirt-color", name: "Sky Blue",     unlockAt: 1,  apply: { shirtColor: "#6bd0ff" } },
  { id: "color-gold",       kind: "shirt-color", name: "Gold",         unlockAt: 2,  apply: { shirtColor: "#ffd166" } },
  { id: "color-lime",       kind: "shirt-color", name: "Lime",         unlockAt: 4,  apply: { shirtColor: "#a3e635" } },
  { id: "color-coral",      kind: "shirt-color", name: "Coral",        unlockAt: 5,  apply: { shirtColor: "#ff7a59" } },
  { id: "color-purple",     kind: "shirt-color", name: "Royal Purple", unlockAt: 7,  apply: { shirtColor: "#9b5cf6" } },
  { id: "color-mint",       kind: "shirt-color", name: "Mint",         unlockAt: 9,  apply: { shirtColor: "#5cf2c2" } },
  { id: "color-red",        kind: "shirt-color", name: "Cherry Red",   unlockAt: 12, apply: { shirtColor: "#ef4444" } },
  { id: "color-blue",       kind: "shirt-color", name: "Ocean Blue",   unlockAt: 15, apply: { shirtColor: "#2563eb" } },
  { id: "color-magenta",    kind: "shirt-color", name: "Magenta",      unlockAt: 19, apply: { shirtColor: "#d946ef" } },
  { id: "color-teal",       kind: "shirt-color", name: "Teal",         unlockAt: 23, apply: { shirtColor: "#14b8a6" } },
  { id: "color-orange",     kind: "shirt-color", name: "Tangerine",    unlockAt: 28, apply: { shirtColor: "#fb923c" } },
  { id: "color-yellow",     kind: "shirt-color", name: "Sunshine",     unlockAt: 34, apply: { shirtColor: "#fde047" } },
  { id: "color-brown",      kind: "shirt-color", name: "Cocoa",        unlockAt: 40, apply: { shirtColor: "#8b5a2b" } },
  { id: "color-white",      kind: "shirt-color", name: "Cloud White",  unlockAt: 48, apply: { shirtColor: "#f9fafb" } },
  { id: "color-black",      kind: "shirt-color", name: "Midnight",     unlockAt: 60, apply: { shirtColor: "#1f2937" } },
];

/** Compute every id whose threshold is <= runs and that was NOT in `priorUnlocked`. */
export function newlyUnlocked(priorUnlocked: string[], priorRuns: number, newRuns: number): UnlockItem[] {
  const already = new Set(priorUnlocked);
  return UNLOCKS.filter(
    (u) => u.unlockAt > priorRuns && u.unlockAt <= newRuns && !already.has(u.id),
  );
}

/** All ids the player should have unlocked given a runs count.
 *  Dev / playtest mode: unlock everything so the avatar editor surfaces
 *  every hat, outfit, face accessory, pattern and shirt colour from the
 *  start. Flip `EVERYTHING_UNLOCKED` to `false` to restore the gated
 *  progression. */
const EVERYTHING_UNLOCKED = true;
export function unlockedIdsFor(runs: number): string[] {
  if (EVERYTHING_UNLOCKED) return UNLOCKS.map((u) => u.id);
  return UNLOCKS.filter((u) => u.unlockAt <= runs).map((u) => u.id);
}

export function unlockById(id: string): UnlockItem | undefined {
  return UNLOCKS.find((u) => u.id === id);
}
